import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Tag, Button, Space } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../../contexts/PermissionContext';
import ProductDetailModal from './ProductDetailModal';
import NameByLang from '../../Component/nameByLang';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const Products = () => {
  const { t } = useTranslation();
  const { hasAnyPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleView = (record: any) => {
    setSelectedProduct(record);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  const handleModalSuccess = () => {
    // Refresh the products list after successful image upload
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  // Remove the getDisplayName function as we'll use NameByLang component

  const getDisplayDescription = (item: any) => {
    return item?.description || '';
  };

  return (
    <AnimatedContainer className="products-page" variant="fadeUp">
      <Table
        tableQueryKey="products"
        tableTitle={t('products.title', 'Products')}
        showSearch={true}
        showDeleteSwitch={false}
        hideFilters={true}
        rowKey="id"
        columns={[
          {
            title: t('products.columns.image', 'Image'),
            dataIndex: 'picture',
            key: 'image',
            width: 80,
            render: (picture: string) => {
              const imageUrl = picture ? `https://driveshield.net/storage/${picture}` : '';
              return imageUrl ? (
                <div className="product-image">
                  <img
                    src={imageUrl}
                    alt="Product"
                    style={{
                      width: 50,
                      height: 50,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '2px solid #f0f0f0',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div
                  className="product-image"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8,
                    color: 'var(--color-text-tertiary)',
                    border: '2px solid #f0f0f0',
                    fontSize: '10px',
                    textAlign: 'center',
                    padding: '4px',
                  }}
                >
                  {t('products.noImage', 'No Image')}
                </div>
              );
            },
          },
          {
            title: t('products.columns.name', 'Name'),
            dataIndex: 'name',
            key: 'name',
            render: (name: any, record: any) => (
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                  <NameByLang
                    name_en={record?.name_en}
                    name_ar={record?.name_ar}
                    name={name}
                    className="bold"
                  />
                </div>
                {record.description && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: '1.4',
                    }}
                  >
                    {getDisplayDescription(record).length > 50
                      ? `${getDisplayDescription(record).substring(0, 50)}...`
                      : getDisplayDescription(record)}
                  </div>
                )}
              </div>
            ),
          },
          {
            title: t('products.columns.sku', 'SKU'),
            dataIndex: 'sku',
            key: 'sku',
            render: (sku: string) => (
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {sku || '-'}
              </div>
            ),
          },
          {
            title: t('products.columns.barcode', 'Barcode'),
            dataIndex: 'barcode',
            key: 'barcode',
            render: (barcode: string) => (
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {barcode || '-'}
              </div>
            ),
          },
          {
            title: t('products.columns.category', 'Category'),
            dataIndex: 'category',
            key: 'category',
            render: (category: any) => (
              <Tag color="blue" style={{ borderRadius: '12px', padding: '4px 12px' }}>
                <NameByLang
                  name_en={category?.name}
                  name_ar={category?.ar_name}
                  fallback={t('products.noCategory', 'No Category')}
                />
              </Tag>
            ),
          },
          // {
          //   title: t('products.columns.buyingPrice', 'Buying Price'),
          //   dataIndex: 'buying_price',
          //   key: 'buyingPrice',
          //   render: (price: string) => (
          //     <span className="price-buying">
          //       {parseFloat(price || '0').toFixed(2)} {t('common.SAR', 'SAR')}
          //     </span>
          //   ),
          // },
          {
            title: t('products.columns.sellingPrice', 'Selling Price'),
            dataIndex: 'selling_price',
            key: 'sellingPrice',
            render: (price: string) => (
              <span className="price-selling">
                {parseFloat(price || '0').toFixed(2)} {t('common.SAR', 'SAR')}
              </span>
            ),
          },
          {
            title: t('products.columns.status', 'Status'),
            dataIndex: 'pos_product',
            key: 'status',
            render: (posProduct: number) => (
              <Tag className={posProduct ? 'status-active' : 'status-inactive'}>
                {posProduct
                  ? t('products.status.active', 'Active')
                  : t('products.status.inactive', 'Inactive')}
              </Tag>
            ),
          },
          {
            title: t('products.columns.actions', 'Actions'),
            key: 'actions',
            width: 120,
            render: (_: any, record: any) => {
              const canView = hasAnyPermission([
                'view_products',
                'view_product',
                'products.read',
                'products.view',
              ]);
              if (!canView) return null;
              return (
                <Space size="small">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    style={{ color: '#1890ff' }}
                    title={t('common.view', 'View')}
                    onClick={() => handleView(record)}
                  />
                  {/* <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ color: '#52c41a' }}
                  title={t('common.edit', 'Edit')}
                  onClick={() => handleEdit(record)}
                /> */}
                  {/* <Popconfirm
                  title={t('messages.deleteConfirm', 'Are you sure you want to delete this product?')}
                  description={t('messages.deleteWarning', 'This action cannot be undone.')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('common.yes', 'Yes')}
                  cancelText={t('common.no', 'No')}
                  okType="danger"
                >
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    title={t('common.delete', 'Delete')}
                    loading={deleteProductMutation.isPending}
                  />
                </Popconfirm> */}
                </Space>
              );
            },
          },
        ]}
        loading={false}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        visible={modalVisible}
        product={selectedProduct}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default Products;
